import {
  AddIcon,
  Flex,
  List,
  Loading,
  TypeAhead,
  Typography,
  Box,
  DismissIcon,
  Button,
  CheckFilledIcon,
} from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import { useState } from 'react';

import {
  useFetchProducts,
  usePosTagsSearch,
} from '@jane/business-admin/data-access';
import { useStoreId } from '@jane/business-admin/hooks';

import type { FormValues } from './form.types';
import { MenuRowCard } from './MenuRowModal';

import styles from './PosTagsCard.module.css';

const PosTagNameOptions = ({
  isLoading,
  onClick,
  query,
  results,
}: {
  isLoading: boolean;
  onClick: (tag: string) => void;
  query: string;
  results: string[];
}) => {
  if (isLoading) {
    return (
      <Flex justifyContent="center" py={16}>
        <Loading />
      </Flex>
    );
  }

  return (
    <List
      label="Tag name search results"
      className={styles['optionsList']}
      px={16}
    >
      {results.length > 0 ? (
        results.map((tag) => (
          <List.Item
            key={tag}
            className={styles['option']}
            px={16}
            py={12}
            onClick={() => onClick(tag)}
          >
            <Typography variant="body-regular">{tag}</Typography>
          </List.Item>
        ))
      ) : (
        <List.Item px={16} py={12} disableHover>
          <Typography variant="body-regular" color="grays-mid">
            No results found
          </Typography>
        </List.Item>
      )}
      {query.length > 0 && !results.includes(query) && (
        <List.Item
          key={query}
          className={styles['option']}
          px={16}
          py={12}
          onClick={() => onClick(query)}
        >
          <Flex alignItems="center" gap={8}>
            <AddIcon color="primary" size="sm" />
            <Typography variant="small-body-semibold" color="primary">
              Create tag &ldquo;{query}&rdquo;
            </Typography>
          </Flex>
        </List.Item>
      )}
    </List>
  );
};

export const PosTagsCard = () => {
  const storeId = useStoreId();
  const {
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<FormValues>();

  const posTagError = (errors.rules as any)?.includes?.[0]?.pos_tags
    ?.message as string | undefined;
  const [query, setQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(
    getValues('rules')?.includes?.[0]?.pos_tags?.[0] ?? ''
  );
  const { data: results = [], isFetching: isFetchingTags } = usePosTagsSearch(
    storeId,
    query
  );

  /** @todo this query needs to be updated when we allow multiple conditions + viewing matching products */
  const { data: productsData, isFetching: isFetchingProducts } =
    useFetchProducts({
      storeId,
      params: { pos_tags: [selectedTag], per_page: 1 },
      enabled: !!selectedTag,
    });
  const productCount = productsData?.pages[0]?.meta.total ?? 0;

  return (
    <MenuRowCard className={styles['posTagsCard']}>
      <Flex flexDirection="column" gap={24}>
        <Box>
          <Typography variant="header-bold" branded>
            POS tag
          </Typography>
          <Typography color="grays-mid">
            Enter the tag exactly as it appears in your POS. Products that
            qualify will automatically populate the row.
          </Typography>
        </Box>
        <Box width="300px">
          <Typography variant="body-bold" mb={12}>
            Tag name
          </Typography>
          {selectedTag ? (
            <Box
              width={300}
              height={48}
              borderRadius="sm"
              borderWidth="1px"
              border="grays-light"
              px={16}
              py={12}
            >
              <Button
                className={styles['selectedTag']}
                size="small"
                label={
                  <Typography variant="small-body-regular">
                    {selectedTag}
                  </Typography>
                }
                endIcon={
                  <DismissIcon style={{ height: '12px', width: '12px' }} />
                }
                variant="tertiary"
                onClick={() => {
                  setQuery('');
                  setSelectedTag('');
                  setValue('rules.includes.0.pos_tags', undefined, {
                    shouldDirty: true,
                  });
                }}
              />
            </Box>
          ) : (
            <TypeAhead
              ariaLabel="Tag name"
              labelHidden
              listAriaLabel="Tag name search results"
              placeholder="e.g. staff_picks"
              width="300px"
              isLayered
              onChange={(val) => setQuery(val)}
              onBlur={() => {
                if (!selectedTag) {
                  setError('rules.includes.0.pos_tags', {
                    message: 'Please fill out this field.',
                  });
                }
              }}
              customOptions={({ closePopover }) => (
                <PosTagNameOptions
                  isLoading={isFetchingTags}
                  onClick={(tag) => {
                    setSelectedTag(tag);
                    setValue('rules.includes.0.pos_tags', [tag], {
                      shouldDirty: true,
                    });
                    clearErrors('rules.includes.0.pos_tags');
                    trigger();
                    closePopover();
                  }}
                  query={query}
                  results={results}
                />
              )}
            />
          )}
          {posTagError && (
            <Typography color="error" mt={16}>
              {posTagError}
            </Typography>
          )}
        </Box>
        {selectedTag &&
          (isFetchingProducts ? (
            <Box position="relative" top={0} p={24}>
              <Loading />
            </Box>
          ) : (
            <Flex flexDirection="column" alignItems="center">
              {productCount > 0 && (
                <>
                  <CheckFilledIcon
                    mb={16}
                    style={{ height: '48px', width: '48px' }}
                  />
                  <Typography variant="body-bold" textAlign="center">
                    Products found
                  </Typography>
                </>
              )}
              <Typography textAlign="center">
                {productCount}{' '}
                {productCount === 1 ? 'product matches' : 'products match'} this
                tag.{' '}
                {productCount > 0
                  ? 'They will appear on this menu row.'
                  : 'Once products have been tagged, they will appear on this menu row.'}
              </Typography>
            </Flex>
          ))}
      </Flex>
    </MenuRowCard>
  );
};
